import React, { useEffect, useState } from 'react';
import { IconValidity, Input } from 'Components';
import { useAppContext, useJourneyContext } from 'Contexts';
import { AttributeInputCallback } from '@forgerock/javascript-sdk';
import { partialMatchRegex } from 'Services';

interface Props extends React.InputHTMLAttributes<HTMLInputElement> {
  failedPolicyMessage?: string;
  callback?: AttributeInputCallback<string | number | boolean>;
  specialPattern?: string;
  displayErrorText?: boolean;
  isValid?: boolean;
  onValidationChange?: (valid: boolean | undefined) => void;
  extraValidationCheck?: () => boolean;
}

type FocusOrChangeEvent = React.FocusEvent<HTMLInputElement> | React.ChangeEvent<HTMLInputElement>;

const MAXIMUM_AGE = 120;

export const LabeledInput: React.FC<Props> = ({
  failedPolicyMessage,
  callback,
  specialPattern,
  className,
  isValid,
  displayErrorText = true,
  onValidationChange,
  extraValidationCheck,
  ...rest
}) => {
  const [valid, setValid] = useState<boolean | undefined>(isValid);
  const { email, setEmail } = useAppContext();
  const { isAtStage } = useJourneyContext();

  const getPattern = (name: string) => {
    const nodeElement = document.getElementsByName(name);
    if (nodeElement.length === 0 || !(nodeElement.item(0) instanceof HTMLInputElement)) return;

    const inputElement = nodeElement.item(0) as HTMLInputElement;
    return inputElement.pattern;
  };

  const computedAutoComplete = callback?.getName() === 'mail' ? 'username' : rest.autoComplete;

  useEffect(() => {
    setValid(isValid);
  }, [isValid]);

  useEffect(() => {
    onValidationChange && onValidationChange(valid);
  }, [valid]);

  const buildDefaultValue = () => {
    if (['Connexion / Registration Page', 'Reset Password Page'].some((stage) => isAtStage(stage as Stage))) {
      callback && callback.setValue(email);
      return email;
    }

    return rest.defaultValue;
  };

  const formatPhoneNumber = (value: string): string =>
    value
      .replace(/\s+/g, '')
      .match(/.{1,2}/g)
      ?.join(' ') || '';

  const applyExtraChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (['Connexion / Registration Page', 'Reset Password Page'].some((stage) => isAtStage(stage as Stage))) {
      setEmail(event.target.value);
    }
    const pattern = getPattern(rest.name || '') || rest.pattern;
    if (pattern && !partialMatchRegex(new RegExp(pattern)).test(`${event.currentTarget.value}`)) {
      event.target.value = event.target.value.slice(0, -1);
    }
    if (rest.name === 'telephoneNumber') {
      event.target.value = formatPhoneNumber(event.target.value);
    }
    return event;
  };

  const buildErrorText = () => {
    switch (rest.name) {
      case 'password':
        return <>Votre mot de passe ne respecte pas les 5 conditions ci-dessous</>;
      case 'sn':
      case 'givenName':
        return <>Seuls les caractères de l&rsquo;alphabet latin, le - et &apos; sont autorisés</>;
      case 'telephoneNumber':
        return <>Le numéro de téléphone doit être composé de 10 chiffres</>;
      case 'postalCode':
        return <>Le code postal doit être composé de 5 chiffres</>;
      case 'custom_birthday':
        return <>La date renseignée n&rsquo;est pas autorisée</>;
      default:
        return <>Le champ renseigné est invalide</>;
    }
  };

  const isValidBirthdate = (event: FocusOrChangeEvent) => {
    const currentDate = new Date();
    const oldestDate = new Date(currentDate.getFullYear() - MAXIMUM_AGE, currentDate.getMonth(), currentDate.getDate());
    const dateProvided = new Date(event.target.value);
    return event.target.value ? dateProvided > oldestDate && dateProvided < currentDate : false;
  };

  const updateValidity = (event: FocusOrChangeEvent) => {
    if (!isAtStage('Registration Page')) return;
    let isValid;
    const pattern = getPattern(rest.name || '') || rest.pattern || specialPattern;
    if (pattern) {
      isValid = event.target.value ? new RegExp(pattern).test(`${event.target.value}`) : undefined;
    } else if (rest.name === 'custom_birthday') {
      isValid = event.target.value ? isValidBirthdate(event) : undefined;
      event.target.setCustomValidity(isValid !== false ? '' : 'DEFAULT');
    }
    if (extraValidationCheck) {
      isValid = isValid && extraValidationCheck();
    }

    setValid(isValid);
  };

  return (
    <label className={`flex flex-col relative grow${rest.hidden === true ? ' hidden' : ''}`}>
      <Input
        {...rest}
        autoComplete={computedAutoComplete}
        placeholder=""
        className={`hide-password-reveal pb-2 pt-6 pr-12 min-h-[58px] outline-navy-blue bg-white disabled:bg-whitesmoke  ${className || ''}
        disabled:outline-none disabled:cursor-auto
        focus:outline-2 focus:outline focus:outline-${valid === false ? 'red' : 'navy-blue'}
        ${valid === false && 'outline outline-1 outline-red'} appearance-none`}
        defaultValue={buildDefaultValue()}
        onFocus={(event) => {
          if (rest.onFocus) {
            rest.onFocus(event);
          }
          setValid(true);
        }}
        onChange={(event) => {
          const extraEvent = applyExtraChange(event);
          if (rest.onChange) {
            rest.onChange(extraEvent);
          }
        }}
        onBlur={(event) => {
          if (rest.onBlur) {
            rest.onBlur(event);
          }
          updateValidity(event);
        }}
      />
      <div className="absolute left-4 text-sonic-silver whitespace-nowrap transition-all text-xs top-2 peer-focus:text-xs peer-focus:top-2 peer-placeholder-shown:top-4 peer-placeholder-shown:text-sm">
        <span>{rest.placeholder}</span> {rest.required && <span className="text-red">*</span>}
      </div>
      {failedPolicyMessage && (
        <div key="error" className="text-red text-xs">
          {failedPolicyMessage}
        </div>
      )}
      {valid !== undefined && (
        <>
          <IconValidity valid={valid} />
          {!valid && displayErrorText && <span className="text-xs text-red pl-2 pt-1.5 font-semibold">{buildErrorText()}</span>}
        </>
      )}
    </label>
  );
};
